import {
  checkEventDashboardPageOriginResponse,
  eventDashboardPageOriginHeaders,
} from './eventDashboardPageOrigin';
import { isReleaseCompatibleHomepagePost } from './homepageQueries';
import { RELEASE_ID, RELEASE_REQUEST_HEADER, RELEASE_RESPONSE_HEADER } from './identity';

export class StaleReleaseError extends Error {
  constructor() {
    super('This page is from an older release. Reload before trying again.');
    this.name = 'StaleReleaseError';
  }
}

export const reloadNavigator = {
  reload: () => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  },
};

function isReleaseCompatibleHomepageQuery(input: RequestInfo | URL, method: string): boolean {
  if (typeof window === 'undefined') return false;
  const requestInput = typeof Request !== 'undefined' && input instanceof Request ? input : undefined;
  try {
    const url = new URL(requestInput?.url ?? String(input), window.location.href);
    if (url.origin !== window.location.origin) return false;
    if (typeof input === 'string') {
      const rawPath = input.split(/[?#]/, 1)[0].replace(/^https?:\/\/[^/@]+/i, '');
      if (input.startsWith('//') || /[\u0000-\u0020\u007f\\]/.test(input) || rawPath !== url.pathname) return false;
    }
    return isReleaseCompatibleHomepagePost(method, url.pathname);
  } catch {
    return false;
  }
}

export async function releaseFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const requestInput = typeof Request !== 'undefined' && input instanceof Request ? input : undefined;
  const method = (init.method || requestInput?.method || 'GET').toUpperCase();
  const headers = eventDashboardPageOriginHeaders(input, init.headers || requestInput?.headers);
  headers.set(RELEASE_REQUEST_HEADER, RELEASE_ID);

  const response = await fetch(input, { ...init, headers });
  checkEventDashboardPageOriginResponse(response);
  const responseRelease = response.headers?.get(RELEASE_RESPONSE_HEADER);
  // A read that succeeds against a newer release is not an error: a document stays in shared
  // cache across a deploy, so its JS legitimately keeps reading the live API. Reloading on that
  // skew costs a full re-render per in-flight request, so only unsafe methods stay fail-closed.
  const staleRelease = responseRelease != null && responseRelease !== RELEASE_ID;
  const isSafeMethod = method === 'GET' || method === 'HEAD';
  if (
    staleRelease &&
    !isSafeMethod &&
    !(response.ok && isReleaseCompatibleHomepageQuery(input, method))
  ) {
    throw new StaleReleaseError();
  }
  return response;
}
